<template>
    <div class="page page-layout task">
        <div class="flexbox-fullheight form-view page-container">
            <header class="page-header">
                <div class="page-header-background"></div>

                <template v-if="!loading">
                    <router-link class="return-to-dashboard" :to="{ name: 'dashboard', hash: '#tasks' }">
                        <icon class="arrow-left-circle-svg large-svg fill-white stroke-white" name="arrow-left" />
                    </router-link>

                    <div class="grid-x">
                        <div class="cell large-8">
                            <nav class="page-breadcrumbs">
                                <ol class="page-breadcrumbs-list" role=list>
                                    <li class="page-breadcrumbs-item"><router-link class="task-navigation-item" :to="{ name: 'dashboard', hash: '#tasks' }">Oversigt</router-link></li>
                                    <li class="page-breadcrumbs-item" aria-current="page">
                                        <icon class="small-svg fill-white" name="chevron-right"></icon>
                                        <icon class="small-svg fill-white" name="task"></icon>
                                        Opgave
                                    </li>
                                </ol>
                            </nav>

                            <h1 class="page-heading">
                                <template v-if="!existingTask">Ny {{ taskType }}</template>
                                <template v-else>{{ task.name }}</template>
                            </h1>

                            <div class="flexbox align-items flexbox-wrap" style="gap: 6px 18px;">
                                <div class="flexbox-center page-header-info">
                                    <icon class="medium-svg fill-white" name="user-reverse" />
                                    {{ task.ownerUserName ? task.ownerUserName : task.ownerName }} <template v-if="existingTask && !task.ownerActive">(INAKTIV)</template>
                                </div>
                                <div class="flexbox-center page-header-info" v-if="supplyTypes.length > 1">
                                    <div class="circle">
                                        <icon :key="selectedSupplyType.systemName" class="svg-invert" :name="selectedSupplyType.systemName" />
                                    </div>
                                    {{ selectedSupplyType.description }}
                                </div>
                                <div class="flexbox-center page-header-info">
                                    <div class="circle">
                                        <icon class="svg-invert" name="location" />
                                    </div>
                                    {{ addressObject.oneLineAddress }}
                                </div>
                            </div>
                        </div>
                        <div class="cell large-2 page-header-actions">
                            <base-split-button v-if="existingTask && !taskClosed && !task.closedDate" @click="close()">
                                <template v-slot:default>Afslut</template>
                                <template v-if="validRemoveTask" v-slot:list>
                                    <ul role="menu">
                                        <li role="none">
                                            <button tabindex="0" role="menuitem" @click="removeTask()">Slet</button>
                                        </li>
                                    </ul>
                                </template>
                            </base-split-button>
                            <base-split-button v-else-if="existingTask && taskClosed || existingTask && task.closedDate" @click="open">Genåbn</base-split-button>
                        </div>
                        <div class="cell large-2 page-header-actions">
                            <div class="save-time-stamp" v-show="savedTimestamp && savedTimestamp !== null">Gemt {{ savedTimestamp | formatTime }}</div>
                        </div>
                    </div>
                </template>
            </header>

            <main class="page-content" v-show="!loading">
                <guide-nav v-if="navLoaded && !getExistingWork" :icons="iconObj" :initStep="step" @changeStep="changedStep" />

                <!-- ------------- step 0 ------------- -->
                <section v-if="step === 0">
                    <div class="grid-x history-header">
                        <div class="cell large-10">
                            <h2>Historik</h2>
                            <button @click="printCaseHistoryHandler()" class="button button-simplified large-width-label">
                                <icon class="bin-svg small-svg fill-dark-gray" name="printer" />
                                Udskriv sagsforløb
                            </button>
                        </div>
                    </div>

                    <history-log v-if="taskLog" :comments="taskLog" :type="'task'" />
                </section>

                <!-- ------------- Generel informations ------------- -->
                <section v-if="step === 1">
                    <h2>Oplysninger</h2>

                    <template v-if="getExistingWork">
                        <hr />

                        <div class="form">
                            <div class="grid-x">
                                <div class="flexbox-center">
                                    <icon class="small-label-svg" name="file-text" />
                                </div>
                                <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                    <label for="select-template">Skabelon<span class="required"></span></label>
                                </div>
                                <div class="auto cell flexbox-start">
                                    <n-filter v-if="!enabled" :list="sortedTaskTemplateList" valueKey="taskId" labelKey="name" :multiSelect="false" :showSearch="true" searchPlaceholder="Søg efter skabelon" @filterChanged="changeTemplateType" v-model="selectedTemplate">
                                        Skabelon
                                        <template v-slot:buttonIcon>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24">
                                                <polygon points="0,0 32,0 16,24" style="fill: rgb(138, 138, 138)"/>
                                            </svg>
                                        </template>
                                    </n-filter>
                                    <div v-else>
                                        <input type="text" id="select-template" v-model="task.templateName" placeholder="Indtast navn" maxlength="255">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <hr />

                    <div class="form">
                        <div class="grid-x" :class="{ 'disabled': enabled }">
                            <div class="flexbox-center">
                                <icon class="small-label-svg" name="user-reverse" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="task-name">Ansvarlig</label>
                            </div>
                            <div class="auto cell flexbox-start">
                                <n-filter :list="organizationUserList" valueKey="userId" labelKey="name" :multiSelect="false" :showSearch="true" searchPlaceholder="Søg efter ansvarlig" @filterChanged="changeUser" v-model="task.ownerUserId">
                                    Ansvarlig
                                    <template v-slot:buttonIcon>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24">
                                            <polygon points="0,0 32,0 16,24" style="fill: rgb(138, 138, 138)"/>
                                        </svg>
                                    </template>
                                </n-filter>
                            </div>
                        </div>
                    </div>

                    <hr />

                    <div class="form">
                        <div class="grid-x" :class="{ 'disabled': enabled }">
                            <div class="flexbox-center">
                                <icon class="small-label-svg" name="task" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="task-name">Opgavenavn</label>
                            </div>
                            <div class="auto cell flexbox-start">
                                <input type="text" id="task-name" v-model="task.name" maxlength="255" @blur="autoSaveTaskInformation">
                            </div>
                        </div>
                    </div>

                    <template v-if="getExistingWork && supplyTypes.length > 1">
                        <hr />

                        <supply-type :class="{ 'disable': task.taskId || selectedTask.activities.length > 0 }" />
                    </template>

                    <hr />

                    <div class="form">
                        <div class="grid-x" :class="{ 'disabled': enabled }">
                            <div class="flexbox-center">
                                <icon class="small-label-svg" name="location" />
                            </div>
                            <div class="cell small-8 medium-4 large-4 flexbox align-items">
                                <label for="task-name">Adresse</label>
                            </div>
                            <div class="auto cell flexbox-start">
                                 <n-input-address
                                    style="width: 100%"
                                    :municipality-code="userInfo.municipalityCodeList ? userInfo.municipalityCodeList.includes(',') ? userInfo.municipalityCodeList.split(',').map(Number) : Number(userInfo.municipalityCodeList) : null"
                                    :is-access-address="true"
                                    @input="addressesSet($event)"
                                    v-model="address"
                                />
                            </div>
                        </div>
                    </div>

                    <div v-if="getExistingWork" class="text-center buttons-modal" style="width: 75%">
                        <a @click="saveNewTask">Opret opgave</a>
                        <router-link :to="{name: 'dashboard'}">
                            Fortryd
                        </router-link>
                    </div>
                </section>

                <!-- ------------- Documents ------------- -->
                <section v-if="step === 2">
                    <n-files :allowedFileTypes="applicationSettings.allowedFileTypes.join(',')" :showList="false" :uploadFunction="uploadFiles" :uploadStatus="uploadStatus" :uploadErrorResponse="uploadErrorResponse">
                        <template v-slot:heading>
                            Dokumenter
                        </template>
                        <template v-slot:description>
                            Upload dine billeder og dokumenter.<br />
                            <em>F.eks. *.pdf, *.jpg, *.doc, *.xls</em>
                        </template>

                        <template v-slot:upload-label>
                            <icon class="large-svg" name="upload" /><br />
                            <strong>Klik for at vælge dine filer</strong><br />
                            <small>... eller træk og slip dem her.</small>
                        </template>
                        <template v-slot:upload-title>
                            Klik for at vælge dine filer eller træk og slip dem her.
                        </template>
                        <template v-slot:upload-saving-singluar>
                            Uploader fil...
                        </template>
                        <template v-slot:upload-saving-plural>
                            Uploader flere filer...
                        </template>
                        <template v-slot:upload-success-singluar>
                            Fil uploadet.
                        </template>
                        <template v-slot:upload-success-plural>
                            Filer uploadet.
                        </template>
                        <template v-slot:upload-failed>
                            Upload fejlede desværre.
                        </template>
                    </n-files>

                    <p class="dfiles-list__empty" v-if="taskDocumentsSorted.length === 0">Der er ikke tilføjet nogle filer endnu.</p>

                    <template v-if="taskDocumentsSorted.length > 0">
                        <div class="document__options">
                            <div class="document__option">
                                <input type="checkbox" id="file-metadata" :checked="taskDocumentsMetadataVisible" @change="documentListMetadataToggle" />
                                <label for="file-metadata">Vis filoplysninger</label>
                            </div>
                        </div>

                        <ul class="document__list">
                            <li class="document__item" v-for="file in taskDocumentsSorted" :key="file.id">
                                <div class="document__image">
                                    <div class="document__loader"></div>
                                    <img v-if="file.contentType.startsWith('image')" :src="file.dataSource" alt="" loading="lazy" />
                                    <icon v-else :name="getIcon(file)" :class="getIcon(file)" />
                                    <button v-if="file.contentType.startsWith('image')" @click="documentOpen(file)"><span class="visually-hidden">Vis billede</span></button>
                                    <button v-else @click="documentDownload(file)"><span class="visually-hidden">Download fil</span></button>
                                    <div class="document__icon" v-if="file.description && !taskDocumentsMetadataVisible" :title="'Kommentar: ' + file.description">
                                        <icon name="info-2" />
                                    </div>
                                </div>
                                <div class="document__info" v-if="taskDocumentsMetadataVisible">
                                    <div class="document__name" :title="file.contentType.startsWith('image') ? $filters.formatDateTime(file.photoTaken) : file.name">
                                        <template v-if="file.contentType.startsWith('image')">{{ file.photoTaken | formatDateTime }}</template>
                                        <template v-else>{{ file.name }}</template>
                                    </div>
                                    <div class="document__date"><strong>Upload:</strong> <time :datetime="file.dateCreated">{{ file.dateCreated | formatDateShort }}</time></div>
                                    <div v-if="file.requestId" class="document__date"><strong><a :href="`/henvendelse/${file.requestId}`" target="_blank">Fra henvendelse</a></strong></div>
                                    <div class="document__description" v-if="file.description"><strong>Kommentar:</strong> <span class="truncate" :title="file.description">{{ file.description }}</span></div>
                                </div>
                                <div class="document__actions">
                                    <button title="Tilføj kommentar" v-if="checkDescriptionAvailability(file) && !file.requestId" @click="commentFile(file)">
                                        <span class="visually-hidden">Tilføj kommentar</span>
                                        <icon name="pencil" />
                                    </button>
                                    <button title="Download fil" @click="documentDownload(file)">
                                        <span class="visually-hidden">Download</span>
                                        <icon name="download-arrow" />
                                    </button>
                                    <button v-if="!file.requestId" class="delete" title="Slet fil" @click="documentRemove(file.id)">
                                        <span class="visually-hidden">Slet</span>
                                        <icon name="trash" />
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </template>
                </section>

                <!-- ------------- Aktiviteter ------------- -->
                <section v-if="step === 3">
                    <div class="grid-x accordion-header">
                        <div class="cell large-4">
                            <h2>Aktiviteter<div class="object-counter" v-if="selectedTask.activities.length > 0" :title="`${selectedTask.activities.length} aktivitet${selectedTask.activities.length > 1 ? 'er' : ''} tilknyttet opgaven`">{{ selectedTask.activities.length }}</div></h2>
                        </div>
                        <div class="cell large-8 flexbox-end" v-if="selectedTask.activities.length > 0" style="margin-bottom: 10px;">
                            <div v-if="task.activities && task.activities.length > 0" @click="closedActivities = !closedActivities" class="flexbox filter-supply closed checkbox-end checkbox-text">
                                <div :class="{'active': closedActivities}" class="flexbox-center task">
                                    <icon v-if="closedActivities" class="small-svg fill-white" name="checkmark"/>
                                </div>
                                <span style="margin-left: 10px;">Vis også godkendte</span>
                            </div>
                        </div>
                    </div>
                    <accordion-activities :selectedObjects="selectedObjects" :resetSelection="resetSelection" :deleteObject="deleteObject" :onObjectChange="redrawObjects" :closedActivities="closedActivities" :setActivityStatus="setActivityStatus" />
                </section>

                <!-- ------------- Requisitions ------------- -->
                <section v-if="step === 4">
                    <div class="grid-x accordion-header">
                        <div class="cell large-6">
                            <h2>Entreprenøropgaver<div class="object-counter" v-if="requisitionsOnTaskList.length > 0" :title="`${requisitionsOnTaskList.length} objekte${requisitionsOnTaskList.length > 1 ? 'r' : ''} tilknyttet opgaven`">{{ requisitionsOnTaskList.length }}</div></h2>
                        </div>
                        <div class="cell large-6 flexbox-end" v-if="requisitionsOnTaskList.length > 0">
                            <button @click="callBackEntrepreneur()" class="button-orange" :disabled="requisitionDisableCallback" v-if="existingTask">
                                <icon class="small-svg fill-orange" name="arrow-left-circle" />
                                Kald tilbage
                            </button>
                            <button @click="disapproveEntrepreneur()" class="button-red" :disabled="requisitionDisableReject" v-if="existingTask">
                                <icon class="cross-svg smaller-svg fill-red" name="cross" />
                                Afvis
                            </button>
                            <button @click="approveEntrepreneur()" class="button-green" style="margin-right: 0px;" :disabled="requisitionDisableApprove" v-if="existingTask">
                                <icon class="checkmark-svg small-svg fill-green" name="checkmark" />
                                Godkend
                            </button>
                        </div>
                    </div>
                    <accordion-entrepreneur v-if="!enabled" :onObjectChange="redrawObjects" />
                    <div v-else>
                        <p>Der kan ikke oprettes nogle entreprenøropgaver for en skabelon</p>
                    </div>
                </section>

                <!-- ------------- Enquiries ------------- -->
                <section v-if="step === 5">
                    <div class="grid-x">
                        <div class="cell large-8">
                            <h2>Henvendelser<div class="object-counter" v-if="task.requests && task.requests.length > 0" :title="`${task.requests.length} henvendelse${task.requests.length > 1 ? 'r' : ''} tilknyttet opgaven`">{{ task.requests.length }}</div></h2>
                        </div>
                    </div>
                    <accordion-enquiries />
                </section>
            </main>
            <div v-show="loading" class="loading-div">
                <h1 class="loading-header">Henter opgave...</h1>
            </div>
        </div>

        <base-map ref="baseMap" :buttonSelected="buttonSelected" :config="mapConfig" :existingTask="existingTask" :existingWork="existingWork" :highlightedObject="highlightedObject" :supplyCode="selectedSupplyType.code" :supplyTypeId="selectedSupplyType.id" @getBoundingBox="boundingBoxMoved" />
        <modal v-if="showModal" :type="modalType" :data="modalData" @close="closeModal" />
    </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters, mapActions } from 'vuex'
import NInputAddress from './../components/shared/NInputAddress.vue'
import baseMap from './../components/shared/map/base-map.vue'
import { NMapConfig, NMapButton, NMapButtonGroup, NMapDraw, NMapFeature, NMapStyle, NMapStyleCircle, NMapLayerWmsGeoServer } from 'ncomponents-map'
import NFiles from './../components/shared/files/files.vue'
import { wait } from '@/mixins/shared/helpers'
import guideNav from './../components/shared/nav/guide-nav'
import supplyType from './../components/shared/supplytype/supplytype'
import accordionActivities from './../components/pages/task/task-accordion-activities'
import accordionEntrepreneur from './../components/pages/task/task-accordion-entrepreneur'
import accordionEnquiries from './../components/pages/task/task-accordion-enquiries'
import modal from './../components/shared/modal/modal.vue'
import historyLog from './../components/shared/history/history.vue'
import BaseSplitButton from '../components/base/BaseSplitButton.vue'
import NFilter from '@/components/base/NFilter.vue'

export default {
    name: 'task',
    components: {
        modal,
        'n-input-address': NInputAddress,
        'base-map': baseMap,
        'NFiles': NFiles,
        'guide-nav': guideNav,
        'supply-type': supplyType,
        'accordion-activities': accordionActivities,
        'accordion-entrepreneur': accordionEntrepreneur,
        'accordion-enquiries': accordionEnquiries,
        'history-log': historyLog,
        'base-split-button': BaseSplitButton,
        NFilter
    },
    data () {
        return {
            closedActivities: false,
            navLoaded: false,
            readyForApprovalRequisitions: 0,
            wktArray: [],
            step: 0,
            selectedTemplate: '',
            task: {
                locationPointWkt: null,
                name: null,
                ownerUserName: null,
                ownerUserId: null
            },
            addressObject: {
                oneLineAddress: '',
                wkt: ''
            },
            address: '',
            templateTypes: [],
            existingTask: false,
            newTemplate: false,
            taskClosed: false,
            // switch
            enabled: false,
            // loading
            loading: false,
            // save
            savedTimestamp: null,
            // modal
            showModal: false,
            modalType: null,
            modalData: null,
            // delete task
            deleteTaskStatus: null,
            // task type
            taskType: '',
            selectedAddress: false,
            taskWkt: '',
            mapConfig: null,
            existingWork: null,
            selectionFilter: {
                selectedLayer: null,
                selectedProperties: []
            },
            buttonSelected: {
                polygonSelection: false,
                pointDrawing: false,
                lineDrawing: false,
                polygonDrawing: false,
                deleteDrawing: false,
                adding: false,
                info: false
            },
            selectedObjects: [],
            addingEnabled: false,
            zoomLimit: 13.5,
            getTaskTimeout: null,

            uploadStatusTimer: null,
            uploadStatus: 'initial',
            uploadErrorResponse: null,
            addressList: [],

            taskDocumentsMetadataVisible: true,
            sortKey: 'dateCreated',
            sortDirection: 'DESC'
        }
    },
    computed: {
        ...mapGetters({
            applicationSettings: 'getApplicationSettings',
            userId: 'getUserId',
            organizationUserList: 'caseWorkerList',
            selectedSupplyType: 'selectedSupplyType',
            selectedTask: 'getSelectedTask',
            taskTemp: 'getTempTask',
            getActiveTaskId: 'getActiveTaskId',
            enquiryUUID: 'getEnquiryUUID',
            getRequestOnTaskList: 'getRequestOnTaskList',
            enquiryAddressToTask: 'getEnquiryAddressToTask',
            selectedActivities: 'getSelectedActivities',
            activitiesAndObjectsList: 'activitiesAndObjectsList',
            taskTemplateList: 'getTaskTemplateList',
            taskDocuments: 'getTaskDocuments',
            existingEnquiriesFromAddress: 'existingEnquiriesFromAddress',
            existingTasksFromAddress: 'existingTasksFromAddress',
            getWfsObjects: 'getWfsObjects',
            enquirySupplyType: 'getEnquirySupplyType',
            requisitionsOnTaskList: 'getRequisitionsOnTaskList',
            taskLog: 'getSelectedTaskLogs',
            userInfo: 'userInfo',
            supplyTypes: 'getSysBusinessAreaTypeList'
        }),
        sortedTaskTemplateList () {
            if (this.taskTemplateList) {
                return [...this.taskTemplateList].filter(template => template.businessAreaTypeId === this.selectedSupplyType.id).sort(function (a, b) {
                    if (a.name.toLowerCase() < b.name.toLowerCase()) { return -1 }
                    if (a.name.toLowerCase() > b.name.toLowerCase()) { return 1 }
                    return 0
                })
            }
            return []
        },
        recentTemplates () {
            const recent = this.getRecentTemplatesFromStorage()
            if (this.taskTemplateList && recent.length > 0) {
                const totalCount = this.sortedTaskTemplateList.length
                const maxRecent = totalCount >= 30 ? 10 : (totalCount >= 15 ? 5 : 0)

                if (maxRecent === 0) return []

                // Filter to only include templates that exist in the current supply type
                return recent
                    .map(templateId => this.taskTemplateList.find(t => t.taskId === templateId && t.businessAreaTypeId === this.selectedSupplyType.id))
                    .filter(template => template !== undefined)
                    .slice(0, maxRecent)
            }
            return []
        },
        otherTemplates () {
            if (this.sortedTaskTemplateList.length < 15) {
                return this.sortedTaskTemplateList
            }
            if (this.recentTemplates.length > 0) {
                const recentIds = this.recentTemplates.map(t => t.taskId)
                return this.sortedTaskTemplateList.filter(template => !recentIds.includes(template.taskId))
            }
            return this.sortedTaskTemplateList
        },
        validRemoveTask () {
            if (this.existingTask) {
                let found = false
                for (let i in this.activitiesAndObjectsList) {
                    if (this.activitiesAndObjectsList[i].requisitionStatus && this.activitiesAndObjectsList[i].requisitionStatus !== null) {
                        found = true
                    }
                }
                if (found) {
                    return false
                } else {
                    return true
                }
            }
            return false
        },
        requisitionDisableCallback () {
            if (this.requisitionsOnTaskList && this.requisitionsOnTaskList.length > 0) {
                let requisitions = this.requisitionsOnTaskList.filter(requisition => requisition.selected && (requisition.status === 'Ny' || requisition.status === 'Accepteret'))

                if (requisitions.length > 0) {
                    return false
                }
            }
            return true
        },
        requisitionDisableReject () {
            if (this.requisitionsOnTaskList && this.requisitionsOnTaskList.length > 0) {
                let requisitions = this.requisitionsOnTaskList.filter(requisition => requisition.selected && (requisition.status === 'Færdigmeldt' || requisition.status === 'Godkendt'))

                if (requisitions.length > 0) {
                    return false
                }
            }
            return true
        },
        requisitionDisableApprove () {
            if (this.requisitionsOnTaskList && this.requisitionsOnTaskList.length > 0) {
                let requisitions = this.requisitionsOnTaskList.filter(requisition => requisition.selected && (requisition.status === 'Færdigmeldt'))

                if (requisitions.length > 0) {
                    return false
                }
            }
            return true
        },
        checkEmptyObjectList () {
            if (this.selectedActivities) {
                return this.selectedActivities.some(act => act.objectList ? act.objectList.length > 0 : false)
            }
            return false
        },
        getExistingWork () {
            if (this.existingTask && this.existingTask === true) {
                return false
            }
            return true
        },
        checkUnderObjectLimit () {
            if (this.task.activities) { return this.task.activities.map(a => a.allObjectsInActivityCount <= 500).every(a => a === true) }
        },
        getSelectedActivity () {
            if (this.selectedActivities) { return this.selectedActivities.filter(a => a.selected).map(a => a.activityId) }
            return null
        },
        checkLayersMultiSelect () {
            const mapServices = this.applicationSettings.kortinfoSettings[this.selectedSupplyType.code].mapServiceList.filter((service) => {
                return service.type === 'ogc' || service.type === 'arcgis' || service.type === 'kortinfo' || service.type === 'ogckortinfo'
            })

            if (mapServices && mapServices.some(service => service.multiSelect === true)) {
                return true
            }

            return false
        },
        iconObj () {
            return [
                { icon: 'history', text: 'Historik', active: true },
                { icon: 'task', text: 'Oplysninger', active: true },
                { icon: 'pictures', text: 'Dokumenter', active: true, counter: this.taskDocuments.length > 0 ? this.taskDocuments.length : null },
                { icon: 'task-send', text: 'Aktiviteter', active: true },
                { icon: 'task-check', text: 'Entreprenøropgaver', active: true, highlight: this.readyForApprovalRequisitions > 0 ? true : null },
                { icon: 'request', text: 'Henvendelser', active: true }
            ]
        },

        taskDocumentsSorted () {
            return [...this.taskDocuments].sort((a, b) => {
                let modifier = 1

                if (this.sortDirection === 'DESC') {
                    modifier = -1
                }

                if (a[this.sortKey] < b[this.sortKey]) {
                    return -1 * modifier
                }
                if (a[this.sortKey] > b[this.sortKey]) {
                    return 1 * modifier
                }

                return 0
            })
        }
    },
    methods: {
        ...mapActions({
            clearTaskDocuments: 'clearTaskDocuments',
            getCurrentUserInfo: 'getCurrentUserInfo',
            getCurrentOrganizationList: 'getCurrentOrganizationList',
            getCaseworkers: 'getCaseworkers',
            getAllTypeLists: 'getAllTypeLists',
            getOrganizationList: 'getAssociatedOrganizationList',
            // other
            setSupplyType: 'setSelectedSupplyTypeFromId',
            getTemplateList: 'getTaskTemplateList',
            getExistingTask: 'getWholeTaskItem',
            openTask: 'setTaskStatusOpen',
            addTaskToRequest: 'addTaskToRequest',
            setEnquiryUUID: 'setEnquiryUUID',
            createTaskRequisition: 'createTaskRequisition',
            deleteTaskAndActivities: 'deleteTaskAndActivities',
            setReportApproved: 'setReportApproved',
            setReportCallBack: 'setReportCallBack',
            // is Template
            setTaskTemplate: 'setTaskTemplate',
            changeSelectedActivityList: 'changeSelectedActivityList',
            // other
            getExistingWorkFromAddress: 'getExistingWorkFromAddress',
            setTaskLogList: 'setTaskLogList',
            printCaseHistoryTask: 'printCaseHistoryTask',
            setTaskObjectList: 'setTaskObjectList',
            createNewTask: 'createNewTask',
            updateTask: 'updateTask',
            addActivityObjects: 'addActivityObjects',
            deleteActivityObjects: 'deleteActivityObjects',
            copyTaskDocuments: 'copyTaskDocuments',
            copyTemplateActivities: 'copyTemplateActivities',
            createNewActivity: 'createNewActivity',

            // FILES
            documentAdd: 'taskDocumentAdd',
            documentRemove: 'taskDocumentRemove',
            documentOpen: 'taskDocumentOpen',
            documentDownload: 'taskDocumentDownload',
            documentDescriptionUpdate: 'taskDocumentDescriptionUpdate'
        }),

        getRecentTemplatesFromStorage () {
            try {
                const stored = localStorage.getItem(`recent${String(this.selectedSupplyType.systemName).charAt(0).toUpperCase() + String(this.selectedSupplyType.systemName).slice(1)}TaskTemplates`)
                return stored ? JSON.parse(stored) : []
            } catch (e) {
                console.error('Error reading recent templates from localStorage:', e)
                return []
            }
        },
        saveRecentTemplateToStorage (templateId) {
            if (!templateId) return

            try {
                let recent = this.getRecentTemplatesFromStorage()

                // Remove if already exists
                recent = recent.filter(id => id !== templateId)

                // Add to beginning
                recent.unshift(templateId)

                // Keep only the 10 most recent
                recent = recent.slice(0, 10)

                localStorage.setItem(`recent${String(this.selectedSupplyType.systemName).charAt(0).toUpperCase() + String(this.selectedSupplyType.systemName).slice(1)}TaskTemplates`, JSON.stringify(recent))
            } catch (e) {
                console.error('Error saving recent template to localStorage:', e)
            }
        },

        redrawObjects (accordionSelected, objectSelected) {
            this.addObjectButtonHandling()
            // To redraw objects we should remember theres is a different in structure when the object is a accordion regarding an activity,
            // or an accordion regarding a report
            // let wktArray = []
            let wktArraySelected = []

            let singleObjectSelected = false

            // if (objectSelected === undefined) {
            let selected = false

            this.wktArray = []
            for (let i in accordionSelected) {
                if (accordionSelected[i] !== undefined) {
                    if (accordionSelected[i].selected) {
                        selected = true
                        if (accordionSelected[i].objectList) {
                            for (let x in accordionSelected[i].objectList) {
                                if (!accordionSelected[i].objectList[x].deleting) {
                                    this.wktArray.push(accordionSelected[i].objectList[x])
                                }
                            }
                        } else if (accordionSelected[i].report && accordionSelected[i].report.objectList) {
                            for (let x in accordionSelected[i].report.objectList) {
                                if (!accordionSelected[i].report.objectList[x].deleting) {
                                    this.wktArray.push(accordionSelected[i].report.objectList[x])
                                }
                            }
                        }
                    }
                }
            }

            // Refresh...
            for (let i in this.activitiesAndObjectsList) {
                for (let x in this.activitiesAndObjectsList[i].objectList) {
                    if (!this.activitiesAndObjectsList[i].objectList[x].deleting) {
                        this.wktArray.push(this.activitiesAndObjectsList[i].objectList[x])
                    }
                }
            }

            // Refresh...
            if (!selected) {
                for (let i in this.activitiesAndObjectsList) {
                    for (let x in this.activitiesAndObjectsList[i].objectList) {
                        if (!this.activitiesAndObjectsList[i].objectList[x].deleting) {
                            this.wktArray.push(this.activitiesAndObjectsList[i].objectList[x])
                        }
                    }
                }
            }

            if (objectSelected) {
                singleObjectSelected = true
                wktArraySelected.push(objectSelected)
                this.wktArray = this.wktArray.filter(wkt => !wkt.deleting)
            }

            if (singleObjectSelected) {
                if (!wktArraySelected[0].deleting) {
                    this.$refs.baseMap.highlightSelectedObjects(wktArraySelected)
                } else {
                    this.$refs.baseMap.highlightSelectedObjects(this.wktArray)
                }
                if (!wktArraySelected[0].deleting) {
                    this.$refs.baseMap.zoomAndHighlightObject(wktArraySelected[0].geomWkt)
                }
            } else if (accordionSelected && accordionSelected.length > 0) {
                accordionSelected.forEach(accordion => {
                    if (accordion.selected) {
                        if (accordion.objectList && accordion.objectList.length > 0) {
                            accordion.objectList.forEach(object => {
                                if (object.geomWkt) {
                                    wktArraySelected.push(object)
                                }
                            })
                        } else if (accordion.report && accordion.report.objectList && accordion.report.objectList.length > 0) {
                            accordion.report.objectList.forEach(object => {
                                if (object.geomWkt) {
                                    wktArraySelected.push(object)
                                }
                            })
                        }
                    }
                })
                this.$refs.baseMap.highlightSelectedObjects(wktArraySelected)
            } else {
                // Refresh...
                this.$refs.baseMap.highlightObjects(this.wktArray)
            }
        },
        async printCaseHistoryHandler () {
            try {
                this.openModal('handler', 'Danner udskrift for sagsforløb...')
                await this.printCaseHistoryTask(this.task.taskId)
                this.closeModal()
            } catch (error) {
                console.error('Print case history', error)
                this.openModal('handler', 'Kunne ikke danne udskrift. Prøv igen')
                setTimeout(() => {
                    this.closeModal()
                }, 2500)
            }
        },
        autoSaveTaskInformation () {
            if (!this.existingTask || this.taskClosed || this.task.closedDate) {
                return
            }

            try {
                // Name and address
                this.getTaskInformation()
                const payload = {
                    taskId: this.task.taskId,
                    taskVersionStamp: this.task.taskVersionStamp,
                    locationPointWkt: this.task.locationPointWkt,
                    name: this.task.name.trim(),
                    address1: this.task.address1,
                    address2: this.task.address2,
                    city: this.task.city,
                    postalCode: this.task.postalCode,
                    addressGuid: this.task.addressGuid,
                    ownerUserId: this.task.ownerUserId,
                    businessAreaTypeId: this.selectedSupplyType.id
                }

                this.updateTask(payload).then(() => {
                    // UPDATE STAMP
                    this.task.taskVersionStamp = this.$store.state.task.selectedTask.taskVersionStamp

                    // Update timestamp only on success
                    this.savedTimestamp = new Date()

                    // HENVENDELSE - Hvordan forgår det her?
                    this.setTaskLogList(this.task.taskId)
                    let request = this.getRequestOnTaskList
                    if (request.length > 0) {
                        let data = {requestId: request[0].requestId, taskId: this.getActiveTaskId}
                        this.addTaskToRequest(data)
                    }
                }).catch((error) => {
                    console.error('Failed to update task information', error)
                })
            } catch (error) {
                console.error('Failed to prepare task information', error)
            }
        },
        getTaskInformation () {
            let fullAddress = ''
            // Address
            if (this.address || this.addressObject.oneLineAddress) {
                fullAddress = this.addressObject.oneLineAddress
                if (this.addressObject.street) {
                    this.task.address1 = this.addressObject.street
                }
                if (this.addressObject.door && !this.addressObject.floor) {
                    this.task.address1 += ', ' + this.addressObject.door
                }
                if (this.addressObject.floor && !this.addressObject.door) {
                    this.task.address1 += ', ' + this.addressObject.floor
                }
                if (this.addressObject.door && this.addressObject.floor) {
                    this.task.address1 += ', ' + this.addressObject.floor + '. ' + this.addressObject.door
                }
                if (this.addressObject.secondStreet) {
                    this.task.address2 = this.addressObject.secondStreet
                }
                this.task.addressGuid = this.addressObject.id
                this.task.city = this.addressObject.city
                this.task.postalCode = this.addressObject.zipCode

                if (this.task.wkt === undefined && this.addressObject.wkt !== undefined && this.addressObject.wkt !== '') {
                    this.task.locationPointWkt = this.addressObject.wkt
                }
            } else {
                this.task.address2 = undefined
                this.task.addressGuid = undefined
                this.task.city = undefined
                this.task.postalCode = undefined
                this.task.address1 = undefined
            }
            // Set default name if not set by user
            if (!this.task.name || this.task.name === '') {
                if (this.task.address1) {
                    this.task.name = fullAddress || this.task.address1
                } else {
                    this.task.name = 'Opgave - ' + this.$filters.formatDateTime(new Date())
                }
            }
            if (this.task.locationPointWkt === '') {
                this.task.locationPointWkt = 'POINT EMPTY'
            }
        },
        // NEW
        async saveNewTask () {
            try {
                // Name and address
                this.getTaskInformation()

                const payload = {
                    locationPointWkt: this.task.locationPointWkt,
                    name: this.task.name.trim(),
                    address1: this.task.address1,
                    address2: this.task.address2,
                    city: this.task.city,
                    postalCode: this.task.postalCode,
                    addressGuid: this.task.addressGuid,
                    ownerUserId: this.task.ownerUserId, // Getter
                    businessAreaTypeId: this.selectedSupplyType.id // Getter
                }

                await this.createNewTask(payload)

                // UPDATE TASK IDs
                this.task.taskId = this.$store.state.task.selectedTask.taskId
                this.task.taskVersionStamp = this.$store.state.task.selectedTask.taskVersionStamp
                this.$store.state.task.activeTaskId = this.$store.state.task.selectedTask.taskId

                // HENVENDELSE - Hvordan forgår det her?
                this.setTaskLogList(this.task.taskId)
                let request = this.getRequestOnTaskList
                if (request.length > 0) {
                    let data = {requestId: request[0].requestId, taskId: this.getActiveTaskId}
                    this.addTaskToRequest(data)
                }

                // Data variable that controls if the task is created or new
                this.existingTask = true

                // Template
                if (this.selectedTemplate !== '') {
                // Save the selected template in localStorage to use when creating new tasks
                    this.saveRecentTemplateToStorage(this.selectedTemplate)
                    // Clone documents from template
                    await this.copyTaskDocuments({fromTaskId: this.selectedTemplate, toTaskId: this.task.taskId})
                    // Create activities from teplate
                    const response = await this.copyTemplateActivities(this.selectedTemplate)
                    await Promise.all(response.map(async (activity) => {
                        const activityObject = {
                            orderTask: activity.orderTask,
                            activityType: activity.activityType,
                            activityTypeId: activity.activityTypeId,
                            activityCode: activity.activityCode,
                            description: activity.description,
                            hyperlink: activity.hyperlink,
                            reference: activity.reference,
                            sentToOrganization: activity.sentToOrganization,
                            sentToOrganizationId: activity.sentToOrganizationId,
                            sentToUser: activity.sentToUser,
                            sentToUserId: activity.sentToUserId,
                            taskId: this.task.taskId
                        }

                        await this.createNewActivity(activityObject)
                    }))
                }

                // Take documents from enquiry if re-routed
                if (this.$store.state.task.taskDocumentsFromEnquiry.length > 0) {
                    this.$store.state.task.taskDocumentsFromEnquiry.forEach((document) => {
                        this.documentAdd(document)
                    })
                }
                this.$store.state.task.taskDocumentsFromEnquiry = []
                // Replace URL to include ID when saving
                this.$router.replace({
                    name: 'taskid',
                    params: {
                        id: this.task.taskId
                    }
                })
                this.setupTask()
            } catch (error) {
                console.error('Failed to create new task', error)
            }
        },
        close () {
            let obj = {
                taskId: this.getActiveTaskId,
                taskVersionStamp: this.task.taskVersionStamp,
                hasRequests: this.task.requests != null ? (this.task.requests.length > 0) : false
            }
            this.openModal('task-modal-close', obj)
        },
        open () {
            let obj = {
                taskId: this.getActiveTaskId,
                taskVersionStamp: this.task.taskVersionStamp
            }
            new Promise((resolve, reject) => {
                resolve(this.openTask(obj))
            }).then((response) => {
                if (response.status === 200) {
                    this.task.taskVersionStamp = response.data.taskVersionStamp
                    this.task.closedDate = null
                    this.taskClosed = false
                }
            })
        },
        openModal (type, data) {
            this.showModal = true
            this.modalType = type
            this.modalData = data
        },
        removeTask () {
            this.modalData = 'Sletter opgave...'
            this.openModal('handler')
            this.deleteTaskAndActivities(this.task.taskId).then((response) => {
                if (response) {
                    this.$router.push({
                        name: 'dashboard'
                    })
                } else {
                    this.modalData = 'kunne ikke slette opgave'
                    setTimeout(() => {
                        this.showModal = false
                    }, 1500)
                }
            }, (error) => {
                console.error('Remove task', error)
                this.modalData = 'kunne ikke slette opgave'
                setTimeout(() => {
                    this.showModal = false
                }, 1500)
            })
        },
        closeModal () {
            if (this.modalData.updating) {
                this.documentDescriptionUpdate(this.modalData)
            }

            this.showModal = false
        },
        async setActivityStatus () {
            this.openModal('handler', 'Rekvirerer...')

            const activities = this.selectedActivities.filter(activity => activity.selected).filter(activity => {
                // Validate om der er en entreprenør assigned
                if (!activity.sentToOrganizationId) {
                    this.displayWarning('Kan ikke rekvirere en aktivitet uden entreprenør')
                    return false
                }

                // Validate om den har nogle objekter
                if (activity.allObjectsInActivityCount ? activity.allObjectsInActivityCount === 0 : true) {
                    this.displayWarning('Du kan ikke rekvirere uden objekter på aktiviteten')
                    return false
                }

                // Validate at det IKKE er en rutine
                if (activity.routineIntervalTypeId) {
                    this.displayWarning('Kan ikke rekvirere en aktivitet, som er en rutine')
                    return false
                }

                // Validate om den allerede er rekvireret
                if (activity.requisitionStatus && (activity.requisitionStatus === 'rekvireret' || activity.requisitionStatus === 'accepteret')) {
                    this.displayWarning('Kan ikke rekvirere en aktivitet, som allerede er rekvireret')
                    return false
                }
                return true
            })

            if (activities.length > 0) {
                await this.createTaskRequisition({taskId: this.task.taskId, activityIdList: activities.map(a => a.activityId)})
                await this.getExistingData()
            }

            this.closeModal()
        },
        displayWarning (message) {
            Vue.prototype.$notify({group: 'info', type: 'warn', title: '', text: message})
        },
        anyRequisitions () {
            let visible = false
            for (let i = 0; i < this.selectedTask.activities.length; i++) {
                if (this.selectedTask.activities[i].requisitionStatus.toLowerCase() === 'rekvireret') {
                    visible = true
                    break
                }
            }
            return visible
        },
        changeTemplateType (item) {
            if (this.sortedTaskTemplateList.find(template => template.taskId === item[0])) {
                if (this.sortedTaskTemplateList.find(template => template.taskId === item[0]).ownerUserId) {
                    // Set responsible
                    this.task.ownerUserId = this.sortedTaskTemplateList.find(template => template.taskId === item[0]).ownerUserId
                    this.task.ownerUserName = this.sortedTaskTemplateList.find(template => template.taskId === item[0]).ownerUserName
                }
                // Choose supply-type
                this.selectedSupplyTypeId = this.sortedTaskTemplateList.find(template => template.taskId === item[0]).businessAreaTypeId
                this.setSupplyType(this.sortedTaskTemplateList.find(template => template.taskId === item[0]).businessAreaTypeId)
            } else {
                console.error('mangler forretningsområde')
            }
        },
        changedStep (result) {
            // Check if task is created
            if (this.existingTask) {
                this.step = result
            }
            if (result === 1) {
                setTimeout(() => {
                    const addressInput = document.querySelector('.n-input-address-input input')
                    if (addressInput) {
                        addressInput.value = this.addressObject.oneLineAddress || ''
                    }
                }, 100)
            }
        },
        changeUser (id) {
            const userId = Array.isArray(id) ? id[0] : id
            const user = this.organizationUserList.find(user => user.userId === userId)
            if (user) {
                this.task.ownerUserName = user.name
            }
            this.autoSaveTaskInformation()
        },
        populateLists (newSupply) {
            if (newSupply !== undefined) {
                for (let i in this.taskTemplateList) {
                    if (this.taskTemplateList[i].BusinessAreaType === newSupply.description) {
                        // this.taskTemplateList.push(this.templateTypes[i])
                    }
                }
            }
        },

        translateAddressData (address) {
            if (!address) {
                return
            }
            return {
                id: address.id,
                street: address.raw.husnummer.vejnavn,
                // href: address.data.href,
                streetNumber: address.houseNumber,
                // floor: address.data.etage,
                // door: address.data.dør,
                zipCode: address.postalCode,
                city: address.raw.husnummer.postnummer.navn,
                secondStreet: address.raw.husnummer.supplerendebynavn.navn,
                oneLineAddress: address.raw.husnummer.vejnavn + ' ' + address.houseNumber,
                type: address.type
            }
        },
        onWktSelected (value) {
            this.task.locationPointWkt = value
        },
        async deleteObject (activityId, objectId) {
            const deletedObject = {
                taskId: this.$store.state.task.selectedTask.taskId,
                activityIdList: [activityId],
                objectIdList: [objectId]
            }
            await this.deleteActivityObjects(deletedObject)
            await this.getBoundedObjects()
        },
        async onLayerObject (object) {
            if (this.selectedActivities && this.selectedActivities.length > 0 && this.selectedActivities.filter(activity => activity.selected).length > 0) {
                const activitiesId = this.selectedActivities.filter(a => a.selected).map(a => a.activityId)
                const newObject = [{
                    taskId: this.$store.state.task.selectedTask.taskId,
                    activityIdList: activitiesId,
                    datasourceId: object.dataSourceId,
                    geomWkt: object.fieldGeometry,
                    keyValue: object.objectKey,
                    name: object.layerName,
                    objectDescription: object.objectDescription
                }]
                await this.addActivityObjects(newObject)
                await this.getBoundedObjects()
                this.closeModal()
            }
        },
        async onLayerObjects (objects) {
            if (this.selectedActivities && this.selectedActivities.length > 0 && this.selectedActivities.filter(activity => activity.selected).length > 0) {
                const activitiesId = this.getSelectedActivity
                const newObjects = objects.map(o => {
                    return {
                        taskId: this.$store.state.task.selectedTask.taskId,
                        activityIdList: activitiesId,
                        datasourceId: o.dataSourceId,
                        geomWkt: o.fieldGeometry,
                        keyValue: o.objectKey,
                        name: o.layerName,
                        objectDescription: o.objectDescription
                    }
                })
                await this.addActivityObjects(newObjects)
                this.selectedActivities.forEach(a => {
                    if (activitiesId.some(id => id === a.activityId)) {
                        a.isAccordionOpen = true
                    }
                })
                await this.getBoundedObjects()
            }
            this.closeModal()
        },
        async getExistingData () {
            this.existingTask = true
            try {
                await this.setEnquiryUUID(null)

                try {
                    await this.getExistingTask(this.$route.params.id ? this.$route.params.id : this.task.taskId)

                    try {
                        const selected = Object.assign({}, this.selectedTask)
                        this.task = selected

                        if (this.task.requisitions && this.task.requisitions.length > 0) {
                            this.readyForApprovalRequisitions = 0

                            this.task.requisitions.forEach(requisition => {
                                if (requisition.report) {
                                    if (requisition.report.report.readyForApproval === true & requisition.report.report.approveDate === null) {
                                        this.readyForApprovalRequisitions++
                                    }
                                }
                            })

                            if (this.readyForApprovalRequisitions > 0) {
                                this.step = 4
                            }
                        }
                        // uses tempTask in Vuex to get this.task for validating save
                        this.$store.state.task.tempTask = this.task
                        if (this.task.address1) {
                            var fullAddress = this.task.address1

                            if (this.task.address2) {
                                fullAddress += ', ' + this.task.address2 + ', '
                            }

                            if (this.task.postalCode) {
                                fullAddress += ', ' + this.task.postalCode
                            }

                            if (this.task.city) {
                                fullAddress += ' ' + this.task.city
                            }
                            this.addressObject.oneLineAddress = fullAddress
                            this.address = this.addressObject.oneLineAddress
                            this.addressObject.id = this.task.addressGuid
                            this.addressObject.city = this.task.city
                            this.addressObject.zipCode = this.task.postalCode
                            this.addressObject.address1 = this.task.address1
                            this.addressObject.street = this.task.address1

                            await this.getExistingWorkFromAddress(this.addressObject)

                            let existingWorkObject = {}

                            if (this.existingEnquiriesFromAddress.length > 0) {
                                existingWorkObject.enquiries = this.existingEnquiriesFromAddress
                            }
                            if (this.existingTasksFromAddress.length > 0) {
                                existingWorkObject.tasks = this.existingTasksFromAddress
                                // Filter out the one task the user is currently operating in
                                if (this.$route.params.id) {
                                    existingWorkObject.tasks = existingWorkObject.tasks.filter(task => task.taskId !== this.$route.params.id)
                                }
                            }

                            this.existingWork = (existingWorkObject.enquiries || existingWorkObject.tasks) ? existingWorkObject : null
                        }

                        this.$store.state.task.activeTaskId = this.task.taskId

                        // Setting inactive responsible
                        if (!this.task.ownerActive) {
                            this.organizationUserList = [{
                                userId: this.enquiry.ownerUserId,
                                displayName: this.enquiry.ownerName + ' (INAKTIV)'
                            }, ...this.organizationUserList]
                        }

                        try {
                            // Changes the supply type here
                            await this.setSupplyType(this.task.businessAreaTypeId)

                            this.selectedSupplyTypeId = this.$store.getters.selectedSupplyType.id

                            this.$nextTick(() => {
                                if (this.getWfsObjects.length > 0) {
                                    this.$refs.baseMap.fitAllObjects(this.getWfsObjects)
                                } else if (this.task.locationPointWkt) {
                                    this.$refs.baseMap.navigateTo(this.task.locationPointWkt)
                                }
                                // Highlight the objects
                                if (this.task.activities && this.task.activities.length > 0) {
                                    this.redrawObjects()
                                }
                            })

                            try {
                                await this.getTemplateList()
                            } catch (err) {
                                console.error('Getting template list', err)
                            }
                        } catch (err) {
                            console.error('Set supply type', err)
                        }
                    } catch (err) {
                        console.error('Get existing work from address', err)
                    }
                } catch (err) {
                    console.error('Getting existing task', err)
                }
            } catch (err) {
                console.error('Setting enquiry uuid', err)
            }
        },
        async approveEntrepreneur () {
            try {
                const response = await this.setReportApproved(true)
                if (response) {
                    let resRequisitionArray = []
                    response.forEach(res => {
                        resRequisitionArray.push(res.data)
                    })
                    this.task.requisitions.forEach(requisition => {
                        let foundRequisition = resRequisitionArray.find(res => res.requisitionId === requisition.requisitionId)
                        if (foundRequisition) {
                            if (requisition.report) {
                                if (requisition.report.report.readyForApproval === true && requisition.report.report.approveDate === null) {
                                    this.readyForApprovalRequisitions--
                                }
                            }
                        }
                    })
                }

                let taskId = null
                if (this.$route.params.id) {
                    taskId = this.$route.params.id
                } else {
                    if (this.$store.state.task.activeTaskId) {
                        taskId = this.$store.state.task.activeTaskId
                    } else {
                        taskId = this.task.taskId
                    }
                }
                try {
                    await this.getExistingTask(taskId)
                } catch (error) {
                    console.error('Getting existing task', error)
                }
            } catch (error) {
                console.error('Set report approved', error)
            }
        },
        async callBackEntrepreneur () {
            try {
                let matchError = false
                if (this.requisitionsOnTaskList && this.requisitionsOnTaskList.length > 0) {
                    this.requisitionsOnTaskList.forEach(req => {
                        if (req.selected) {
                            if (req.status !== 'Ny' && req.status !== 'Accepteret') {
                                matchError = true
                            }
                        }
                    })
                }
                if (!matchError) {
                    await this.setReportCallBack()
                } else {
                    this.$notify({
                        group: 'info',
                        type: 'warn',
                        title: '',
                        text: 'Kun entreprenøropgaver med status "Ny" og "Accepteret" kan blive kaldt tilbage'
                    })
                }
            } catch (err) {
                console.error('error call back entrepreneur', err)
            }
        },
        async disapproveEntrepreneur () {
            try {
                await this.setReportApproved(false)
                let taskId = null
                if (this.$route.params.id) {
                    taskId = this.$route.params.id
                } else {
                    if (this.$store.state.task.activeTaskId) {
                        taskId = this.$store.state.task.activeTaskId
                    } else {
                        taskId = this.task.taskId
                    }
                }
                try {
                    await this.getExistingTask(taskId)
                } catch (error) {
                    console.error('error getting existing task', error)
                }
            } catch (error) {
                console.error('error disapprove entrepreneur', error)
            }
        },
        initTask () {
            this.taskType = 'opgave'
            this.setTaskTemplate(false)
            this.$store.state.task.activityIndex = null
            this.$store.state.task.selectedTask = {name: null, logs: [], activities: [], requisitions: [], requests: []}
            this.$store.state.task.removeActivities = []
            this.$store.state.task.wfsObjects = []
        },
        getAllUserInfo () {
            let promise1 = this.getCurrentUserInfo()
            let promise2 = this.getCurrentOrganizationList()
            let promise3 = this.getAllTypeLists()

            Promise.all([promise1, promise2, promise3]).then((values) => {
                this.getOrganizationList()
                this.getCaseworkers()
                return true
            })
        },
        setupMap () {
            this.mapConfig = new NMapConfig()
            const markerColor = '#2C2C96' // PURPLE
            const drawColor = 'skyblue'
            const finishedColor = 'darkseagreen'

            // ************************** Tilknyt objekter **************************
            const mapButtonAddObjects = new NMapButton({
                content: '<svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="-50 -50 650 600" style="enable-background:new 0 0 512 512" xml:space="preserve"> <g transform="translate(1 1)"><g fill="white"><path d="M489.305,185.463c-8.354-8.309-21.861-8.272-30.17,0.081L202.687,443.379c-33.271,33.271-87.308,33.271-120.641-0.045 c-33.308-33.325-33.308-87.362,0.004-120.674L346.089,57.234c20.772-20.771,54.543-20.771,75.375,0.045 c20.826,20.826,20.826,54.593-0.005,75.425L202.727,351.434c-0.014,0.014-0.026,0.03-0.04,0.044 c-8.333,8.287-21.8,8.276-30.116-0.04c-8.33-8.33-8.33-21.831,0-30.161l105.58-105.602c8.33-8.332,8.329-21.84-0.003-30.17 c-8.332-8.33-21.84-8.329-30.17,0.003l-105.579,105.6c-24.991,24.991-24.991,65.507,0.002,90.499 c24.992,24.992,65.508,24.992,90.501,0c0.029-0.029,0.052-0.06,0.08-0.089l218.646-218.646c37.494-37.494,37.494-98.276,0-135.77 c-37.499-37.472-98.277-37.472-135.749,0L51.84,292.53C1.906,342.464,1.906,423.509,51.876,473.504 c50.003,49.977,131.049,49.977,181.022,0.004l256.489-257.875C497.695,207.279,497.658,193.772,489.305,185.463z"/></g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> <g> </g> </svg>',
                title: 'Tilknyt til aktiviteter',
                cls: 'add-object-button'
            })

            this.mapConfig.on('getFeaturesBegin', e => {
                if (this.buttonSelected.adding) {
                    this.openModal('handler', 'Tilføjer objekterne..')
                    setTimeout(() => {
                        this.closeModal()
                    }, 2500)
                }
            })

            mapButtonAddObjects.on('click', () => {
                this.mapConfig.cancelDraw()
                if (!this.buttonSelected.adding && this.addingEnabled) {
                    this.setButtons('adding')
                    this.mapConfig.on('getFeatures', (e) => {
                        if (this.buttonSelected.adding) {
                            const keyInfos = []
                            let keyInfoPromises = []
                            let includesPoint = false
                            e.layers.forEach((layer) => {
                                layer.features.forEach((feature) => {
                                    let featurePromises = []

                                    if (!feature.geometry) {
                                        // Get geometry from kortinfo
                                        featurePromises.push(new Promise((resolve, reject) => {
                                            const mapService = this.applicationSettings.kortinfoSettings[this.selectedSupplyType.code].mapServiceList.find((service) => {
                                                return (service.type === 'ogc' || service.type === 'arcgis' || service.type === 'kortinfo' || service.type === 'ogckortinfo') && service.kortInfoSite && service.kortInfoPage
                                            })

                                            if (mapService && feature.layerId) {
                                                let url = 'https://services.drift.kortinfo.net/kortinfo/services/Feature.ashx?Site=' + mapService.kortInfoSite + '&Page=' + mapService.kortInfoPage + '&output=geojson&srs=EPSG:25832&layer=' + feature.layerId + '&filter=%3Cequals%3E%3Cstring%3E' + feature.properties[0].value + '%3C/string%3E%3CfeatureId%20/%3E%3C/equals%3E'

                                                fetch(url, {
                                                    headers: {
                                                        'Content-Type': 'application/json'
                                                    }
                                                })
                                                    .then(response => response.json())
                                                    .then(data => {
                                                        feature.geometry = new NMapFeature({
                                                            sourceType: 'geoJsonFeature',
                                                            source: data.features[0],
                                                            projection: 'EPSG:25832',
                                                            cargo: { layerName: layer.layer.manager.name }
                                                        })
                                                        resolve()
                                                    })
                                                    .catch((error) => {
                                                        console.error('Error:', error)
                                                    })
                                            }
                                        }))
                                    }

                                    keyInfoPromises.push(Promise.all(featurePromises).then(() => {
                                        if (feature.geometry && feature.geometry.wkt('EPSG:25832').includes('POINT')) {
                                            includesPoint = true
                                        }

                                        let objectLayer = layer.layer.cargo ? layer.layer.cargo.layers.find(ol => ol.id === feature.layerId.toString()) : null

                                        if (!objectLayer) {
                                            const mapService = this.applicationSettings.kortinfoSettings[this.selectedSupplyType.code].mapServiceList.find((service) => {
                                                return (service.type === 'ogc' || service.type === 'arcgis' || service.type === 'kortinfo' || service.type === 'ogckortinfo') && service.layers.find(layer => layer.id === feature.layerId || layer.name === feature.layerName || (feature.geometry && feature.geometry.cargo && layer.name === feature.geometry.cargo.layerName))
                                            })

                                            if (mapService && mapService.layers) {
                                                objectLayer = mapService.layers.find(layer => layer.id === feature.layerId || layer.name === feature.layerName || (feature.geometry && feature.geometry.cargo && layer.name === feature.geometry.cargo.layerName))
                                            }
                                        }

                                        if (objectLayer) {
                                            const keyValue = feature.properties.find(prop => prop.name === objectLayer.objectIdPropertyName)
                                            // TODO: Skal laves om til at være jSON strengen den tager beskrivelse fra.
                                            let props = null

                                            if (objectLayer.displayObjectProperties) {
                                                props = JSON.stringify(objectLayer.displayObjectProperties.map((prop) => {
                                                    let property = null

                                                    if (prop.name && feature.properties && feature.properties.find(featureProp => featureProp.name === prop.name)) {
                                                        property = {
                                                            Name: prop.name,
                                                            Value: feature.properties.find(featureProp => featureProp.name === prop.name).value
                                                        }
                                                    }

                                                    return property
                                                }))
                                            }

                                            const keyInfo = {
                                                dataSourceId: objectLayer.dataSourceId,
                                                fieldGeometry: feature.geometry.wkt('EPSG:25832'),
                                                objectDescription: props !== null ? props : null,
                                                objectKey: keyValue.value,
                                                layerName: layer.layer instanceof NMapLayerWmsGeoServer ? objectLayer.name : (feature.layerName ? feature.layerName : objectLayer.name)
                                            }

                                            keyInfos.push(keyInfo)
                                        }
                                    }))
                                })
                            })
                            Promise.all(keyInfoPromises).then(() => {
                                if (includesPoint) {
                                    const points = keyInfos.filter(k => k.fieldGeometry.includes('POINT'))
                                    points.forEach(p => this.onLayerObject(p))
                                } else {
                                    keyInfos.forEach(k => this.onLayerObject(k))
                                }
                            })
                        }
                    })
                } else {
                    this.setButtons('')
                }
            })
            this.mapConfig.addButton(mapButtonAddObjects)

            if (this.checkLayersMultiSelect) {
                const mapButtonAddObjectsPolygon = new NMapButton({
                    content: '<svg x="0px" y="0px" viewBox="-50 0 760 700" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="m458.398438 319.914062c0 6.898438-5.601563 12.5-12.5 12.5h-112.796876v112.8125c0 6.898438-5.601562 12.5-12.5 12.5-6.898437 0-12.5-5.601562-12.5-12.5v-112.8125h-112.804687c-6.898437 0-12.5-5.601562-12.5-12.5 0-6.902343 5.601563-12.5 12.5-12.5h112.804687v-112.8125c0-6.914062 5.601563-12.5 12.5-12.5 6.898438 0 12.5 5.585938 12.5 12.5v112.8125h112.796876c6.898437 0 12.5 5.597657 12.5 12.5zm0 0"/><path fill="white" d="m627.5 100.023438c6.898438 0 12.5-5.601563 12.5-12.5v-75.023438c0-6.898438-5.601562-12.5-12.5-12.5h-75.023438c-6.898437 0-12.5 5.601562-12.5 12.5v25.015625h-40.828124c-6.898438 0-12.5 5.59375-12.5 12.5 0 6.898437 5.601562 12.5 12.5 12.5h40.828124v25.007813c0 6.898437 5.601563 12.5 12.5 12.5h25.007813v170.527343h-25.007813c-6.898437 0-12.5 5.601563-12.5 12.5v75.027344c0 6.898437 5.601563 12.5 12.5 12.5h25.007813v169.40625h-25.007813c-6.898437 0-12.5 5.59375-12.5 12.5v25h-169.367187v-25c0-6.90625-5.59375-12.5-12.5-12.5h-75.023437c-6.898438 0-12.5 5.59375-12.5 12.5v25h-170.570313v-25c0-6.90625-5.59375-12.5-12.5-12.5h-25v-169.40625h25c6.90625 0 12.5-5.601563 12.5-12.5v-75.027344c0-6.898437-5.59375-12.5-12.5-12.5h-25v-170.527343h25c6.90625 0 12.5-5.601563 12.5-12.5v-25.007813h170.570313v25.007813c0 6.898437 5.601562 12.5 12.5 12.5h75.023437c6.90625 0 12.5-5.601563 12.5-12.5v-25.007813h40.828125c6.914062 0 12.5-5.601563 12.5-12.5 0-6.90625-5.585938-12.5-12.5-12.5h-40.828125v-25.015625c0-6.898438-5.59375-12.5-12.5-12.5h-75.023437c-6.898438 0-12.5 5.601562-12.5 12.5v25.015625h-170.570313v-25.015625c0-6.898438-5.59375-12.5-12.5-12.5h-75.015625c-6.898438 0-12.5 5.601562-12.5 12.5v75.023438c0 6.898437 5.601562 12.5 12.5 12.5h25.015625v170.527343h-25.015625c-6.898438 0-12.5 5.601563-12.5 12.5v75.027344c0 6.898437 5.601562 12.5 12.5 12.5h25.015625v169.40625h-25.015625c-6.898438 0-12.5 5.59375-12.5 12.5v75.015625c0 6.898438 5.601562 12.5 12.5 12.5h75.015625c6.90625 0 12.5-5.601562 12.5-12.5v-25.015625h170.570313v25.015625c0 6.898438 5.601562 12.5 12.5 12.5h75.023437c6.90625 0 12.5-5.601562 12.5-12.5v-25.015625h169.367187v25.015625c0 6.898438 5.601563 12.5 12.5 12.5h75.023438c6.898438 0 12.5-5.601562 12.5-12.5v-75.015625c0-6.90625-5.601562-12.5-12.5-12.5h-25.015625v-169.40625h25.015625c6.898438 0 12.5-5.601563 12.5-12.5v-75.027344c0-6.898437-5.601562-12.5-12.5-12.5h-25.015625v-170.527343zm-331.914062-75.023438h50.023437v50.023438h-50.023437zm-270.585938 50.023438v-50.023438h50.015625v50.023438zm0 270.554687v-50.027344h50.015625v50.027344zm50.015625 269.421875h-50.015625v-50.015625h50.015625zm270.59375 0h-50.023437v-50.015625h50.023437zm269.390625-50.015625v50.015625h-50.023438v-50.015625zm0-269.433594v50.027344h-50.023438v-50.027344zm-50.023438-220.527343v-50.023438h50.023438v50.023438zm0 0"/><path d="m455.320312 62.515625c-6.902343 0-12.507812-5.601563-12.507812-12.5 0-6.90625 5.589844-12.5 12.496094-12.5h.011718c6.902344 0 12.5 5.59375 12.5 12.5 0 6.898437-5.597656 12.5-12.5 12.5zm0 0"/></svg>',
                    title: 'Masse-Tilknyt til aktiviteter',
                    cls: 'add-objects-polygon-button'
                })
                mapButtonAddObjectsPolygon.on('click', () => {
                    if (this.buttonSelected.pointDrawing || this.buttonSelected.lineDrawing || this.buttonSelected.polygonDrawing || this.buttonSelected.deleteDrawing) {
                        this.mapConfig.cancelDraw()
                    }
                    if (this.buttonSelected.polygonSelection) {
                        this.setButtons('')
                        this.mapConfig.cancelDraw()
                    } else if (this.addingEnabled) {
                        this.setButtons('polygonSelection')
                        const mapDrawPolygonObjects = new NMapDraw({
                            type: 'Polygon',
                            style: new NMapStyle({
                                fillColor: drawColor,
                                strokeColor: drawColor
                            }),
                            drawStyle: new NMapStyle({
                                fillColor: drawColor,
                                fillOpacity: 0.5,
                                strokeColor: drawColor
                            }),
                            vertexStyle: new NMapStyleCircle({
                                fillOpacity: 0,
                                strokeColor: markerColor,
                                strokeWidth: 2,
                                radius: 7
                            })
                        })
                        mapDrawPolygonObjects.on('done', e => {
                            const data = {
                                wkt: e.feature.wkt('EPSG:25832'),
                                selectedActivity: this.getSelectedActivity,
                                taskId: this.task.taskId,
                                getBoundedObjects: this.getBoundedObjects
                            }
                            this.openModal('selection-filter-modal', data)
                            let drawLayer = this.mapConfig.layers.find(l => l.manager.name === 'draw')
                            drawLayer.clear()
                            this.setButtons('')
                        })
                        this.mapConfig.draw(mapDrawPolygonObjects)
                    }
                })
                this.mapConfig.addButton(mapButtonAddObjectsPolygon)
            }

            const buttons = []
            buttons.push({
                id: 'point',
                content: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="-200 -125 505 505"><path fill="white" d="M51.855,179.298c1.926,0,3.879-0.098,5.841-0.32c13.776-1.553,26.114-8.37,34.76-19.217c8.646-10.838,12.551-24.392,10.998-38.169c-3.187-28.174-28.396-48.927-57.377-45.74c-28.422,3.204-48.945,28.946-45.749,57.377C3.301,159.494,25.448,179.298,51.855,179.298zM49.743,108.428c0.728-0.089,1.456-0.124,2.175-0.124c9.72,0,17.877,7.296,18.978,16.963c0.568,5.068-0.87,10.066-4.057,14.06c-3.187,3.994-7.731,6.515-12.8,7.074c-10.643,1.278-19.963-6.453-21.135-16.839C31.724,119.08,39.278,109.609,49.743,108.428z"/></svg>',
                title: 'Tegn punkt',
                cls: 'point-draw-button'
            })
            buttons.push({
                id: 'line',
                content: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="-100 -100 712.01 712.01"><g fill="white"><path d="M55.747,400.511C25.013,400.511,0,425.524,0,456.258c0,30.744,25.013,55.747,55.747,55.747 c30.744,0,55.747-25.003,55.747-55.747C111.493,425.524,86.481,400.511,55.747,400.511z M55.747,476.798 c-11.319,0-20.54-9.231-20.54-20.55c0-11.319,9.212-20.531,20.54-20.531s20.54,9.221,20.54,20.54S67.066,476.798,55.747,476.798z"/><path d="M456.253,0.005c-30.734,0-55.747,25.003-55.747,55.747s25.013,55.747,55.747,55.747 c30.744,0,55.756-25.013,55.756-55.747S487.006,0.005,456.253,0.005z M456.253,76.292c-11.329,0-20.531-9.212-20.531-20.54 s9.202-20.54,20.531-20.54c11.319,0,20.54,9.212,20.54,20.54S467.582,76.292,456.253,76.292z"/><path d="M385.716,101.409L101.395,385.731c-6.875,6.866-6.875,18.013,0,24.889c3.433,3.433,7.934,5.159,12.444,5.159 c4.51,0,9.011-1.726,12.444-5.159l284.321-284.322c6.885-6.866,6.885-18.013,0-24.889 C403.739,94.524,392.592,94.524,385.716,101.409z"/></g></svg>',
                title: 'Tegn linje',
                cls: 'line-draw-button'
            })
            buttons.push({
                id: 'polygon',
                content: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="-100 -50 711.987 611.987"><g fill="white"><path d="M51.855,179.298c1.926,0,3.879-0.098,5.841-0.32c13.776-1.553,26.114-8.37,34.76-19.217c8.646-10.838,12.551-24.392,10.998-38.169c-3.187-28.174-28.396-48.927-57.377-45.74c-28.422,3.204-48.945,28.946-45.749,57.377C3.301,159.494,25.448,179.298,51.855,179.298zM49.743,108.428c0.728-0.089,1.456-0.124,2.175-0.124c9.72,0,17.877,7.296,18.978,16.963c0.568,5.068-0.87,10.066-4.057,14.06c-3.187,3.994-7.731,6.515-12.8,7.074c-10.643,1.278-19.963-6.453-21.135-16.839C31.724,119.08,39.278,109.609,49.743,108.428z"/><path d="M345.336,121.574c1.926,0,3.879-0.098,5.841-0.328c28.431-3.213,48.945-28.955,45.74-57.359c-3.169-28.191-28.44-49.033-57.377-45.758c-28.422,3.204-48.936,28.955-45.74,57.377C296.756,101.771,318.911,121.574,345.336,121.574zM343.223,50.696c0.728-0.089,1.456-0.133,2.175-0.133c9.729,0,17.877,7.305,18.951,16.989c1.189,10.465-6.373,19.945-16.839,21.126c-10.545,1.314-19.972-6.444-21.135-16.847C325.196,61.357,332.749,51.877,343.223,50.696z"/><path d="M511.653,377.446c-3.187-28.183-28.68-48.944-57.368-45.749c-13.767,1.553-26.106,8.37-34.751,19.217c-8.646,10.838-12.56,24.384-10.998,38.151c2.956,26.274,25.111,46.077,51.528,46.077c1.935,0,3.888-0.107,5.841-0.32c13.767-1.553,26.106-8.37,34.751-19.209C509.301,404.767,513.207,391.222,511.653,377.446zM475.047,395.172c-3.187,3.994-7.731,6.515-12.791,7.075c-10.616,1.296-19.972-6.453-21.144-16.856c-0.577-5.06,0.87-10.048,4.048-14.042c3.187-3.994,7.74-6.515,12.809-7.083c0.737-0.08,1.465-0.124,2.184-0.124c9.711,0,17.851,7.296,18.942,16.981C479.672,386.189,478.225,391.186,475.047,395.172z"/><path d="M49.468,390.734c-28.422,3.204-48.936,28.955-45.74,57.377c2.965,26.274,25.111,46.086,51.51,46.086c1.935,0,3.897-0.115,5.858-0.328c13.776-1.553,26.114-8.37,34.76-19.217c8.646-10.838,12.551-24.392,10.998-38.169C103.667,408.3,78.636,387.405,49.468,390.734zM70.221,454.218c-3.187,3.994-7.731,6.515-12.809,7.083c-10.598,1.101-19.954-6.462-21.126-16.865c-1.181-10.474,6.382-19.954,16.839-21.135c0.728-0.089,1.447-0.124,2.166-0.124c9.737,0,17.904,7.296,18.987,16.981C74.845,445.226,73.407,450.223,70.221,454.218z"/><path d="M125.752,129.279c1.056,0,2.113-0.107,3.178-0.311l145.715-28.662c8.876-1.749,14.655-10.368,12.915-19.244c-1.749-8.876-10.341-14.637-19.244-12.915L122.6,96.809c-8.876,1.749-14.664,10.368-12.915,19.244C111.23,123.865,118.082,129.279,125.752,129.279z"/><path d="M366.586,127.681c-8.495,3.107-12.871,12.516-9.764,21.019l61.052,166.797c2.423,6.64,8.699,10.758,15.383,10.758c1.873,0,3.764-0.32,5.628-1.003c8.504-3.107,12.88-12.516,9.764-21.019l-61.043-166.797C384.508,128.942,375.063,124.566,366.586,127.681z"/><path d="M382.36,378.023l-254.052,37.059c-8.956,1.314-15.152,9.631-13.847,18.587c1.189,8.149,8.193,14.025,16.191,14.025c0.79,0,1.58-0.062,2.388-0.169l254.052-37.059c8.956-1.314,15.161-9.631,13.856-18.587C399.633,382.931,391.298,376.807,382.36,378.023z"/><path d="M52.734,189.204c-0.062,0-0.124,0-0.186,0c-9.045,0.098-16.306,7.501-16.208,16.555l1.713,158.533c0.107,8.992,7.421,16.208,16.386,16.208c0.062,0,0.124,0,0.186,0c9.045-0.098,16.306-7.501,16.208-16.555L69.12,205.412C69.014,196.42,61.699,189.204,52.734,189.204z"/></g></svg>',
                title: 'Tegn polygon',
                cls: 'polygon-draw-button'
            })
            buttons.push({
                id: 'delete',
                content: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="-20 -20 140 140"><g fill="white"><rect x="61.73" y="29.65" width="5.91" height="56.78"/><rect x="48.16" y="29.65" width="5.91" height="56.78"/><rect x="34.59" y="29.65" width="5.91" height="56.78"/><path d="M21,85.17A11.45,11.45,0,0,0,32.46,96.61H69.77A11.45,11.45,0,0,0,81.2,85.17V19.47H21ZM75.29,25.38V85.21a5.49,5.49,0,0,1-5.5,5.49H32.43a5.5,5.5,0,0,1-5.5-5.49V25.38Z"/><path d="M67.63,12.69V10.56a8,8,0,0,0-8-8h-17a8,8,0,0,0-8,8v2.13H21V18.6H81.2V12.69Zm-27.13,0V10.56a2.14,2.14,0,0,1,2.14-2.14H59.58a2.13,2.13,0,0,1,2.15,2.14v2.13Z"/></g></svg>',
                title: 'Slet',
                cls: 'delete-draw-button'
            })

            const drawButtons = new NMapButtonGroup({
                buttons: buttons,
                direction: 'primary',
                axis: 'horizontal'
            })
            drawButtons.on('click', e => {
                switch (e.button) {
                case 'point':
                    if (this.buttonSelected.pointDrawing) {
                        this.setButtons('')
                        this.mapConfig.cancelDraw()
                        break
                    }
                    this.setButtons('pointDrawing')
                    const mapDrawPoint = new NMapDraw({
                        type: 'Point',
                        drawStyle: new NMapStyleCircle({
                            fillOpacity: 0,
                            strokeColor: markerColor,
                            strokeWidth: 2,
                            radius: 7
                        })
                    })
                    mapDrawPoint.on('done', e => {
                        e.feature.style = new NMapStyleCircle({
                            fillColor: finishedColor,
                            fillOpacity: 0.5,
                            strokeColor: finishedColor,
                            strokeWidth: 2,
                            radius: 7
                        })

                        let drawLayer = this.mapConfig.layers.find(l => l.manager.name === 'draw')
                        drawLayer.clear()
                        drawLayer.addFeature(e.feature)

                        // Add new drawed object
                        this.$refs.baseMap.doneNewDrawedObject(e.feature.wkt('EPSG:25832'))
                        this.setButtons('')
                    })
                    this.mapConfig.draw(mapDrawPoint)
                    break
                case 'line':
                    if (this.buttonSelected.lineDrawing) {
                        this.setButtons('')
                        this.mapConfig.cancelDraw()
                        break
                    }
                    this.setButtons('lineDrawing')
                    const mapDrawLine = new NMapDraw({
                        type: 'LineString',
                        style: new NMapStyle({
                            fillColor: drawColor,
                            strokeColor: drawColor

                        }),
                        drawStyle: new NMapStyle({
                            fillColor: drawColor,
                            fillOpacity: 0.5,
                            strokeColor: drawColor
                        }),
                        vertexStyle: new NMapStyleCircle({
                            fillOpacity: 0,
                            strokeColor: markerColor,
                            strokeWidth: 2,
                            radius: 7
                        })
                    })
                    mapDrawLine.on('done', e => {
                        e.feature.style.strokeColor = finishedColor

                        let drawLayer = this.mapConfig.layers.find(l => l.manager.name === 'draw')
                        drawLayer.clear()
                        drawLayer.addFeature(e.feature)

                        // Add new drawed object
                        this.$refs.baseMap.doneNewDrawedObject(e.feature.wkt('EPSG:25832'))
                        this.setButtons('')
                    })
                    this.mapConfig.draw(mapDrawLine)
                    break
                case 'polygon':
                    if (this.buttonSelected.polygonDrawing) {
                        this.setButtons('')
                        this.mapConfig.cancelDraw()
                        break
                    }
                    this.setButtons('polygonDrawing')
                    const mapDrawPolygon = new NMapDraw({
                        type: 'Polygon',
                        style: new NMapStyle({
                            fillColor: drawColor,
                            strokeColor: drawColor
                        }),
                        drawStyle: new NMapStyle({
                            fillColor: drawColor,
                            fillOpacity: 0.5,
                            strokeColor: drawColor
                        }),
                        vertexStyle: new NMapStyleCircle({
                            fillOpacity: 0,
                            strokeColor: markerColor,
                            strokeWidth: 2,
                            radius: 7
                        })
                    })
                    mapDrawPolygon.on('done', e => {
                        e.feature.style.fillColor = finishedColor
                        e.feature.style.fillOpacity = 0.5
                        e.feature.style.strokeColor = finishedColor

                        let drawLayer = this.mapConfig.layers.find(l => l.manager.name === 'draw')
                        drawLayer.clear()
                        drawLayer.addFeature(e.feature)

                        // Add new drawed object
                        this.$refs.baseMap.doneNewDrawedObject(e.feature.wkt('EPSG:25832'))
                        this.setButtons('')
                    })
                    this.mapConfig.draw(mapDrawPolygon)
                    break
                case 'delete':
                    if (this.buttonSelected.deleteDrawing) {
                        this.setButtons('')
                        this.mapConfig.cancelDraw()
                        break
                    }
                    this.setButtons('deleteDrawing')
                    const handler = e => {
                        // Are there more one object?
                        const features = []
                        let includesPoint = false
                        e.layers.forEach(l => {
                            l.features.forEach(f => {
                                if (f.geometry) {
                                    if (f.geometry.wkt('EPSG:25832').includes('POINT')) {
                                        includesPoint = true
                                    }
                                    features.push(f)
                                }
                            })
                        })
                        if (includesPoint) {
                            const points = features.filter((k) => {
                                if (k.fieldGeometry) {
                                    return k.fieldGeometry.includes('POINT')
                                }
                                if (k.geometry.wkt('EPSG:25832')) {
                                    return k.geometry.wkt('EPSG:25832').includes('POINT')
                                }
                                return false
                            })
                            if (points) {
                                points.forEach(points => this.$refs.baseMap.deleteObject(points.properties.find(p => p.name === 'Id').value))
                            }
                        } else {
                            features.forEach(k => this.$refs.baseMap.deleteObject(k.properties.find(p => p.name === 'Id').value))
                        }
                        this.setButtons('')
                    }
                    this.mapConfig.once('getFeatures', handler)
                    // let drawLayer = this.mapConfig.layers.find(l => l.manager.name === 'draw')
                    // drawLayer.clear()
                    break
                }
            })
            this.mapConfig.addButton(drawButtons)

            this.mapConfig.on('moved', (e) => {
                this.addObjectButtonHandling()
                clearTimeout(this.getTaskTimeout)
                this.getTaskTimeout = setTimeout(() => this.getBoundedObjects(), 20)
            })
        },
        setupTask () {
            this.loading = true
            if (!this.$route.params.id) {
                this.step = 1
            } else {
                this.step = 3
            }
            if (!this.enquirySupplyType && this.applicationSettings.uiSettings && this.applicationSettings.uiSettings.DefaultBusinessAreaTypeId !== null) {
                this.setSupplyType(this.applicationSettings.uiSettings.DefaultBusinessAreaTypeId)
            }
            this.initTask() // Just init the task object
            return new Promise((resolve, reject) => {
                if (!this.$store.getters.userInfo.userId) {
                    resolve(this.getAllUserInfo()) // GLOBAL
                } else {
                    resolve(true)
                }
            }).then(() => {
                if (this.$route.params.id) {
                    return this.getExistingData() // HVIS DEN HAR ET URL SÅ HENTER DEN TASK HER
                } else { // HAR INGEN URL, DERFOR NY TASK
                    this.$store.state.task.selectedActivities = this.selectedTask.activities
                    this.task.ownerUserId = this.userId
                    this.task.ownerUserName = this.userId ? this.organizationUserList.find(user => user.userId === this.userId).name : ''
                    this.$store.state.task.activeTaskId = ''
                    this.$store.state.task.activitiesAndObjectsList = []

                    // HVIS DEN HAR ADDRESS
                    if (this.enquiryAddressToTask && this.enquiryAddressToTask.addressGuid) {
                        let address = this.enquiryAddressToTask
                        this.selectedSupplyTypeId = this.$store.getters.selectedSupplyType.id
                        if (address.oneLineAddress) {
                            this.addressObject.oneLineAddress = address.oneLineAddress
                            this.address = address.oneLineAddress
                        }
                        if (address.address1) {
                            this.addressObject.street = address.address1
                        }
                        if (address.address2) {
                            this.addressObject.secondStreet = address.address2
                        }
                        if (address.addressGuid) {
                            this.addressObject.id = address.addressGuid
                        }
                        if (address.city) {
                            this.addressObject.city = address.city
                        }
                        if (address.postalCode) {
                            this.addressObject.zipCode = address.postalCode
                        }
                        if (address.wkt) {
                            // Draw on map
                            this.addressObject.wkt = address.wkt
                            this.$refs.baseMap.navigateTo(address.wkt)
                        }

                        // TEMPLATE
                        this.getTemplateList().then(() => {
                            this.populateLists(this.$store.getters.selectedSupplyType)
                        })

                        // ANDRE OPGAVER PÅ DENNE ADDRESSE
                        new Promise((resolve, reject) => {
                            resolve(this.getExistingWorkFromAddress(this.addressObject))
                        }).then(() => {
                            let existingWorkObject = {}
                            if (this.existingEnquiriesFromAddress.length > 0) {
                                existingWorkObject.enquiries = this.existingEnquiriesFromAddress
                            }
                            if (this.existingTasksFromAddress.length > 0) {
                                existingWorkObject.tasks = this.existingTasksFromAddress
                                // Filter out the one task the user is currently operating in
                                if (this.$route.params.id) {
                                    existingWorkObject.tasks = existingWorkObject.tasks.filter(task => task.taskId !== this.$route.params.id)
                                }
                            }
                            this.existingWork = (existingWorkObject.enquiries || existingWorkObject.tasks) ? existingWorkObject : null
                        })
                    } else { // INGEN ADDRESSE
                        this.$store.state.dashboard.enquiryTask = []
                        // SUPPLY TYPE
                        if (this.enquirySupplyType) {
                            this.setSupplyType(this.enquirySupplyType)
                        }
                        // TEMPLATE
                        this.getTemplateList().then(() => {
                            this.populateLists()
                        })
                    }
                }
            }).then(() => {
                setTimeout(() => {
                    const addressInput = document.querySelector('.n-input-address-input input')
                    if (addressInput) {
                        addressInput.value = this.addressObject.oneLineAddress || ''
                    }
                }, 100)
                this.navLoaded = true
                this.loading = false
            })
        },
        addStylingSelectedButton (button) {
            document.querySelector(`${button}`).classList.add('selected')
        },
        setButtons (val) {
            Object.keys(this.buttonSelected).forEach(button => {
                if (button === val) {
                    this.buttonSelected[button] = true
                } else {
                    this.buttonSelected[button] = false
                }
            })
        },
        highlightedObject (objects) {
            this.selectedObjects = objects
        },
        resetSelection () {
            this.selectedObjects = []
            this.redrawObjects()
        },
        addObjectButtonHandling () {
            this.$nextTick(() => {
                if (this.mapConfig) {
                    // const zoomLevel = this.mapConfig.getZoom()
                    const activitiesSelected = this.activitiesAndObjectsList.some(activity => activity.selected === true)
                    if (!activitiesSelected) { // zoomLevel <= this.zoomLimit ||
                        this.mapConfig.cancelDraw()
                        this.setButtons('')
                        this.addingEnabled = false
                        document.querySelector('.add-object-button').classList.add('disabledAddingButtons')
                        if (document.querySelector('.add-objects-polygon-button')) {
                            document.querySelector('.add-objects-polygon-button').classList.add('disabledAddingButtons')
                        }
                    } else {
                        if (document.querySelector('.add-objects-polygon-button')) {
                            document.querySelector('.add-objects-polygon-button').classList.remove('disabledAddingButtons')
                        }
                        document.querySelector('.add-object-button').classList.remove('disabledAddingButtons')
                        this.addingEnabled = true
                    }
                }
            })
        },
        async boundingBoxMoved (data) {
            if (this.$store.state.task.selectedTask.taskId) {
                const payload = {
                    id: this.$store.state.task.selectedTask.taskId,
                    bBox: data.bounds
                }

                await this.setTaskObjectList(payload)
                // Highlight new objects
                this.redrawObjects(this.selectedActivities.filter(a => a.selected))
            }
        },
        async getBoundedObjects (reload) {
            if (this.$store.state.task.selectedTask.taskId || reload) {
                const boundingBox = this.mapConfig.getBoundingBox()
                const bounds = {
                    xMin: boundingBox.bottomLeft.project('EPSG:25832').x,
                    yMin: boundingBox.bottomLeft.project('EPSG:25832').y,
                    xMax: boundingBox.topRight.project('EPSG:25832').x,
                    yMax: boundingBox.topRight.project('EPSG:25832').y
                }
                const data = {id: this.$store.state.task.selectedTask.taskId, bBox: bounds}
                await this.setTaskObjectList(data)
                // Highlight new objects
                this.redrawObjects(this.selectedActivities.filter(a => a.selected))
            }
        },
        exportInspectionsToExcel () {
            this.convertInspectionsToExcel(this.task.taskId)
        },

        uploadFiles (element) {
            const startTime = performance.now()
            let endTime = startTime

            clearTimeout(this.uploadStatusTimer)
            this.uploadStatus = 'saving'

            Object.values(element.files).forEach((file) => {
                const objectData = {
                    data: new FormData(),
                    dataType: file.type,
                    file: file
                }

                objectData.data.append('content', file, file.name)

                this.documentAdd(objectData)
                    .then((result) => {
                        endTime = performance.now()

                        return result
                    })
                    .then(wait(250 - (endTime - startTime > 250 ? 250 : endTime - startTime))) // Make sure spinner is shown at least 250ms
                    .then(() => {
                        this.savedTimestamp = new Date()
                        this.uploadStatus = 'success'
                        document.getElementById(this.$children.find(child => child.$options.name === 'NFiles').$children.find(child => child.inputId).inputId).value = ''
                        this.uploadStatusTimer = setTimeout(() => {
                            this.uploadStatus = 'initial'
                        }, 3500)
                    })
                    .catch((error) => {
                        this.uploadErrorResponse = error
                        this.uploadStatus = 'failed'
                        this.uploadStatusTimer = setTimeout(() => {
                            this.uploadStatus = 'initial'
                        }, 5000)
                    })
            })
        },
        getIcon (file) {
            // Determine icon by mime type
            let iconName = 'unknown'

            if (file.contentType.indexOf('pdf') > -1) {
                iconName = 'pdf'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.document') > -1) {
                iconName = 'doc'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.presentation') > -1) {
                iconName = 'ppt'
            } else if (file.contentType.indexOf('officedocument') > -1 && file.contentType.indexOf('.sheet') > -1) {
                iconName = 'xls'
            } else if (file.contentType.indexOf('csv') > -1) {
                iconName = 'csv'
            } else if (file.contentType.indexOf('plain') > -1) {
                iconName = 'txt'
            } else if (file.contentType.indexOf('xml') > -1) {
                iconName = 'xml'
            }

            return iconName
        },
        checkDescriptionAvailability (file) {
            if (!file.description && this.hideDescription) {
                return false
            } else {
                return true
            }
        },
        commentFile (file) {
            this.openModal('document-modal-description', file)
        },

        addressesSet (object) {
            this.selectedAddress = true
            this.addressObject = this.translateAddressData(object)

            this.taskWkt = `POINT (${object.utmx} ${object.utmy})`
            // Navigate to position on map
            this.$refs.baseMap.navigateTo(this.taskWkt)
            // Save position
            this.task.locationPointWkt = this.taskWkt
            // Getting existing enquiries and tasks on the address
            if (this.addressObject) {
                if (this.addressObject.streetNumber) {
                    this.addressObject.street += ' ' + this.addressObject.streetNumber
                }

                new Promise((resolve, reject) => {
                    resolve(this.getExistingWorkFromAddress(this.addressObject))
                }).then(() => {
                    let existingWorkObject = {}

                    if (this.existingEnquiriesFromAddress.length > 0) {
                        existingWorkObject.enquiries = this.existingEnquiriesFromAddress
                    }
                    if (this.existingTasksFromAddress.length > 0) {
                        existingWorkObject.tasks = this.existingTasksFromAddress

                        // Filter out the one enquiry the user is currently operating in
                        if (this.$route.params.id) {
                            existingWorkObject.tasks = existingWorkObject.tasks.filter(task => task.taskId !== this.$route.params.id)
                        }
                    }
                    this.existingWork = (existingWorkObject.enquiries || existingWorkObject.tasks) ? existingWorkObject : null
                })
            }
            this.autoSaveTaskInformation()
            // }
        },

        documentListMetadataToggle () {
            this.taskDocumentsMetadataVisible = event.target.checked
            localStorage.setItem('taskDocumentsMetadataVisible', event.target.checked)
        }
    },
    watch: {
        selectedSupplyType (val, oldVal) {
            const selectedTemplate = this.sortedTaskTemplateList.find(template => template.taskId === this.selectedTemplate)

            if (selectedTemplate) {
                const selectedTemplateSupplyTypeId = selectedTemplate.businessAreaTypeId
                if (oldVal.id !== val.id && selectedTemplateSupplyTypeId && val.id !== selectedTemplateSupplyTypeId) {
                    this.selectedTemplate = ''
                }
            }
        },
        enabled (val, oldVal) {
            if (val !== oldVal) {
                this.changeSelectedActivityList(val)
                this.setTaskTemplate(val)
            }
        },
        accordionSelected (val) {
            this.redrawObjects()
        },
        buttonSelected: {
            handler (val) {
                if (val.adding) {
                    this.addStylingSelectedButton('.add-object-button')
                } else {
                    document.querySelector('.add-object-button').classList.remove('selected')
                }

                if (val.pointDrawing) {
                    this.addStylingSelectedButton('button.point-draw-button')
                } else {
                    document.querySelector('button.point-draw-button').classList.remove('selected')
                }

                if (val.lineDrawing) {
                    this.addStylingSelectedButton('button.line-draw-button')
                } else {
                    document.querySelector('button.line-draw-button').classList.remove('selected')
                }

                if (val.polygonDrawing) {
                    this.addStylingSelectedButton('button.polygon-draw-button')
                } else {
                    document.querySelector('button.polygon-draw-button').classList.remove('selected')
                }

                if (val.deleteDrawing) {
                    this.addStylingSelectedButton('button.delete-draw-button')
                } else {
                    document.querySelector('button.delete-draw-button').classList.remove('selected')
                }

                if (document.querySelector('.add-objects-polygon-button')) {
                    if (val.polygonSelection) {
                        this.addStylingSelectedButton('.add-objects-polygon-button')
                    } else {
                        document.querySelector('.add-objects-polygon-button').classList.remove('selected')
                    }
                }
            },
            deep: true
        },

        taskLog () {
            this.savedTimestamp = new Date()
        }
    },
    created () {
        this.clearTaskDocuments()
        this.setupMap()
        this.setupTask()
        if (localStorage.getItem('taskDocumentsMetadataVisible') === 'true') {
            this.taskDocumentsMetadataVisible = true
        }
    }
}
</script>

<style lang="scss">
@import '../scss/components/dfiles.scss';
@import '../scss/components/loader';
@import '../scss/pages/task';

.page-layout .n-autocomplete {
    display: flex;

    label {

        @include sm-up() {
            width: calc(66.66667% + 20px + 20px);
        }
        @include md-up() {
            width: calc(33.33333% + 20px + 20px);
        }

        svg {
            vertical-align: top;
        }
    }
    &-wrapper {
        flex: 1 1 0px;
    }
}
</style>



// WEBPACK FOOTER //
// src/pages/task.vue